// GridOverlay.js
// SVG grid overlay replicating the physical pancake art griddle.
//
// Physical griddle layout:
//   - 1/2" square grid cells
//   - Full griddle: 26 columns (0–25) × 22 rows (A–V)
//   - Left  circle: columns 0–12, rows F–Q (12-cell diameter, offset half-cell)
//   - Right circle: columns 13–25, rows F–Q (same)
//   - Rows A–E above circles, R–V below
//
// Two modes:
//   fullGriddle=false  →  single circle (12×12 cells), current behavior
//   fullGriddle=true   →  full griddle (26×22 cells) with both circles
//
// Props:
//   size         {number}  pixel diameter of the circular crop area (12 cells)
//   color        {string}  grid line + circle color, default '#00ffff'
//   opacity      {number}  0–1, default 0.45
//   showCircle   {bool}    draw reference circle(s), default true
//   showLabels   {bool}    draw row/column labels, default true
//   side         {"left"|"right"|null}  which griddle side — sets column numbering
//   fullGriddle  {bool}    show full griddle layout with both circles

import React from 'react';

const ALL_ROW_LABELS = ['0','A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W'];
const CIRCLE_ROW_LABELS = ALL_ROW_LABELS.slice(5, 17); // F–Q

export default function GridOverlay({
  size, color = '#00ffff', opacity = 0.45,
  showCircle = true, showLabels = true,
  side = null, fullGriddle = false,
}) {
  if (!size) return null;

  const cellSize  = size / 12; // one cell in pixels (derived from circle diameter)
  const labelSize = Math.max(7, Math.min(11, cellSize * 0.55));

  // ── FULL GRIDDLE MODE ──────────────────────────────────────────────────────
  if (fullGriddle) {
    const cols = 26;
    const rows = 24;
    const w = cols * cellSize;
    const h = rows * cellSize;

    // Grid lines at every cell boundary
    const vLines = [];
    for (let i = 0; i <= cols; i++) {
      vLines.push(<line key={`fv${i}`} x1={i * cellSize} y1={0} x2={i * cellSize} y2={h} />);
    }
    const hLines = [];
    for (let i = 0; i <= rows; i++) {
      hLines.push(<line key={`fh${i}`} x1={0} y1={i * cellSize} x2={w} y2={i * cellSize} />);
    }

    // Circle positions — offset +1/2 cell on the x-axis (circles bleed half a
    // cell into columns 0/12 and 13/25 respectively).
    // Left center:  col 6.5, row 12.  Right center: col 19.5, row 12.
    // (Row 0 is the dead row above A, so F–Q are at indices 6–17, center = 12)
    const circleRadius = 6 * cellSize;
    const leftCx  = 6.5 * cellSize;
    const rightCx = 19.5 * cellSize;
    const circleCy = 12 * cellSize;

    // Labels
    const labels = [];
    if (showLabels) {
      // Column labels along the top
      for (let i = 0; i <= cols - 1; i++) {
        labels.push(
          <text key={`fc${i}`}
            x={(i + 0.5) * cellSize} y={labelSize + 1}
            textAnchor="middle" fill={color} fontSize={labelSize}
            opacity={Math.min(opacity * 1.8, 0.85)}
            fontFamily="monospace" fontWeight="bold">
            {i}
          </text>
        );
      }
      // Row labels along the left
      for (let i = 0; i < rows; i++) {
        labels.push(
          <text key={`fr${i}`}
            x={labelSize * 0.5} y={(i + 0.5) * cellSize + labelSize * 0.35}
            textAnchor="middle" fill={color} fontSize={labelSize}
            opacity={Math.min(opacity * 1.8, 0.85)}
            fontFamily="monospace" fontWeight="bold">
            {ALL_ROW_LABELS[i]}
          </text>
        );
      }
    }

    // ── Center-line scoring (ruler marks) ─────────────────────────────────
    // Horizontal center: between rows K and L = y at row index 11 (K=10, L=11)
    // Vertical center: between columns 12 and 13 = x at col 13
    // Physical: 1/2" cells. Ticks every 1/8" (= cellSize/4) with smaller ticks
    // every 1/16" (= cellSize/8). Centered on the line, perpendicular to it.
    const scoringMarks = [];
    const sixteenthCell = cellSize / 8;  // 1/16" in pixels
    const tickHalf     = cellSize * 0.11; // uniform small tick
    const centerLineOp = Math.min(opacity * 2.2, 1);

    // Vertical center line scoring (x = 13 * cellSize, ticks are horizontal)
    const vCenterX = 13 * cellSize;
    for (let y = 0; y <= h; y += sixteenthCell) {
      scoringMarks.push(
        <line key={`vs${y.toFixed(1)}`}
          x1={vCenterX - tickHalf} y1={y} x2={vCenterX + tickHalf} y2={y}
          stroke={color} strokeWidth={0.7} opacity={centerLineOp} />
      );
    }

    // Horizontal center line scoring (y = 12 * cellSize, ticks are vertical)
    const hCenterY = 12 * cellSize;
    for (let x = 0; x <= w; x += sixteenthCell) {
      scoringMarks.push(
        <line key={`hs${x.toFixed(1)}`}
          x1={x} y1={hCenterY - tickHalf} x2={x} y2={hCenterY + tickHalf}
          stroke={color} strokeWidth={0.7} opacity={centerLineOp} />
      );
    }

    // Highlight active side with a subtle fill
    const activeHighlight = side ? (
      <rect
        x={side === 'left' ? 0 : 13 * cellSize}
        y={6 * cellSize}
        width={13 * cellSize}
        height={12 * cellSize}
        fill={color}
        opacity={0.04}
        rx={4}
      />
    ) : null;

    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
        <defs>
          <filter id="circle-glow-full" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {activeHighlight}
        <g stroke={color} strokeWidth={0.8} opacity={opacity} fill="none">
          {vLines}
          {hLines}
        </g>
        {scoringMarks}
        {labels}
        {/* Center dots */}
        <circle cx={leftCx} cy={circleCy} r={cellSize * 0.15}
          fill="none" stroke={color} strokeWidth={0.7} opacity={Math.min(opacity * 2.2, 1)} />
        <circle cx={rightCx} cy={circleCy} r={cellSize * 0.15}
          fill="none" stroke={color} strokeWidth={0.7} opacity={Math.min(opacity * 2.2, 1)} />
        {showCircle && (
          <>
            <circle cx={leftCx} cy={circleCy} r={circleRadius}
              fill="none" stroke={color} strokeWidth={3}
              opacity={side === 'right' ? Math.min(opacity * 1.2, 0.5) : Math.min(opacity * 2.2, 1)}
              filter="url(#circle-glow-full)" />
            <circle cx={rightCx} cy={circleCy} r={circleRadius}
              fill="none" stroke={color} strokeWidth={3}
              opacity={side === 'left' ? Math.min(opacity * 1.2, 0.5) : Math.min(opacity * 2.2, 1)}
              filter="url(#circle-glow-full)" />
          </>
        )}
      </svg>
    );
  }

  // ── SINGLE CIRCLE MODE (default) ──────────────────────────────────────────
  const half = size / 2;
  const cx   = half;
  const cy   = half;
  const colStart = side === 'right' ? 13 : 0;

  const vLines = [];
  for (let x = (half % cellSize) + cellSize / 2; x <= size; x += cellSize) {
    vLines.push(<line key={`v${x.toFixed(2)}`} x1={x} y1={0} x2={x} y2={size} />);
  }
  const hLines = [];
  for (let y = cy % cellSize; y <= size; y += cellSize) {
    hLines.push(<line key={`h${y.toFixed(2)}`} x1={0} y1={y} x2={size} y2={y} />);
  }

  const colLabels = [];
  const rowLabels = [];
  if (showLabels && side) {
    for (let i = 0; i <= 12; i++) {
      colLabels.push(
        <text key={`col${i}`} x={i * cellSize} y={labelSize + 1}
          textAnchor="middle" fill={color} fontSize={labelSize}
          opacity={Math.min(opacity * 1.8, 0.85)}
          fontFamily="monospace" fontWeight="bold">
          {colStart + i}
        </text>
      );
    }
    for (let i = 0; i < 12; i++) {
      rowLabels.push(
        <text key={`row${i}`} x={labelSize * 0.4} y={(i + 0.5) * cellSize + labelSize * 0.35}
          textAnchor="middle" fill={color} fontSize={labelSize}
          opacity={Math.min(opacity * 1.8, 0.85)}
          fontFamily="monospace" fontWeight="bold">
          {CIRCLE_ROW_LABELS[i]}
        </text>
      );
    }
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
      <defs>
        <filter id="circle-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g stroke={color} strokeWidth={0.8} opacity={opacity} fill="none">
        {vLines}
        {hLines}
      </g>
      {/* Horizontal center scoring (between K and L, at y = half) */}
      {(() => {
        const tick = cellSize * 0.11;
        const step = cellSize / 8; // 1/16"
        const op = Math.min(opacity * 2.2, 1);
        const marks = [];
        for (let x = 0; x <= size; x += step) {
          marks.push(
            <line key={`cs${x.toFixed(1)}`}
              x1={x} y1={half - tick} x2={x} y2={half + tick}
              stroke={color} strokeWidth={0.7} opacity={op} />
          );
        }
        return marks;
      })()}
      {/* Center dot */}
      <circle cx={cx} cy={cy} r={cellSize * 0.15}
        fill="none" stroke={color} strokeWidth={0.7} opacity={Math.min(opacity * 2.2, 1)} />
      {colLabels}
      {rowLabels}
      {showCircle && (
        <circle cx={cx} cy={cy} r={half}
          fill="none" stroke={color} strokeWidth={3}
          opacity={Math.min(opacity * 2.2, 1)}
          filter="url(#circle-glow)" />
      )}
    </svg>
  );
}
